import { useEffect, useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, Truck } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CarrierPnLPage() {
  const { token } = useAuth();
  const [days, setDays] = useState(90);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/programs/reports/carrier-pnl?days=${days}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) { setData({ error: 'Admin only' }); return; }
      setData(await r.json());
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [days]);

  if (loading) return <div className="p-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  if (data?.error) return <div className="p-8 text-red-600">{data.error}</div>;

  return (
    <div className="max-w-6xl mx-auto space-y-6" data-testid="carrier-pnl-page">
      <div className="flex justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight flex items-center gap-2">
            <Truck className="w-6 h-6 text-indigo-500" /> Carrier P&amp;L
          </h1>
          <p className="text-slate-500 mt-1">Transportation profit and margin per gateway.</p>
        </div>
        <div className="flex gap-2">
          {[30, 90, 180, 365].map(d => (
            <Button key={d} size="sm" variant={days === d ? 'default' : 'outline'} onClick={() => setDays(d)}>{d}d</Button>
          ))}
        </div>
      </div>

      <div className="grid sm:grid-cols-3 gap-4">
        <Card><CardContent className="p-4"><div className="text-xs text-slate-500">Total labels</div><div className="text-2xl font-bold mt-1">{data.totals.labels}</div></CardContent></Card>
        <Card><CardContent className="p-4"><div className="text-xs text-slate-500">Total revenue</div><div className="text-2xl font-bold text-emerald-600 mt-1">${data.totals.revenue.toLocaleString(undefined, { minimumFractionDigits: 2 })}</div></CardContent></Card>
        <Card><CardContent className="p-4"><div className="text-xs text-slate-500">Total profit</div><div className="text-2xl font-bold text-indigo-600 mt-1">${data.totals.profit.toLocaleString(undefined, { minimumFractionDigits: 2 })}</div></CardContent></Card>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base">Per-Carrier Breakdown</CardTitle><CardDescription>Sorted by revenue, last {days} days.</CardDescription></CardHeader>
        <CardContent>
          {data.carriers.length === 0 ? (
            <p className="text-sm text-slate-500 py-4 text-center">No labels in this window.</p>
          ) : (
            <table className="w-full text-sm">
              <thead><tr className="text-slate-500 border-b">
                <th className="text-left py-2">Gateway</th>
                <th className="text-right">Labels</th>
                <th className="text-right">Revenue</th>
                <th className="text-right">Net cost</th>
                <th className="text-right">Profit</th>
                <th className="text-right">Margin</th>
                <th className="text-right">Insurance $</th>
              </tr></thead>
              <tbody>
                {data.carriers.map(c => (
                  <tr key={c.gateway} className="border-b last:border-b-0" data-testid={`carrier-row-${c.gateway}`}>
                    <td className="py-2 font-mono text-xs">{c.gateway}</td>
                    <td className="text-right tabular-nums">{c.labels}</td>
                    <td className="text-right tabular-nums">${c.revenue.toFixed(2)}</td>
                    <td className="text-right tabular-nums text-slate-500">${c.net_cost.toFixed(2)}</td>
                    <td className={`text-right tabular-nums font-semibold ${c.transportation_profit >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>${c.transportation_profit.toFixed(2)}</td>
                    <td className="text-right tabular-nums">{c.gross_margin_pct}%</td>
                    <td className="text-right tabular-nums text-slate-500">${c.insurance_attached.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
